import React from 'react';
import type { View } from '../../types';
import { MoreIcon } from '../icons';
import { useStrategicPillar } from '../../lib/api/hooks/strategic-pillar';

interface StrategicPillarDetailPageProps {
  strategicPillarId: string;
  setView: (view: View) => void;
}

const StrategicPillarDetailPage: React.FC<StrategicPillarDetailPageProps> = ({ strategicPillarId, setView }) => {
  const { data: strategicPillarData, isLoading, error } = useStrategicPillar(strategicPillarId);
  const article = strategicPillarData?.data;

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-wine-100"></div>
          <p className="mt-4 text-sm text-gray-500">Loading strategic pillar...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="text-center p-8 bg-white rounded-lg">
        <h2 className="text-xl font-bold">Strategic Pillar not found</h2>
        <p className="text-sm text-gray-500 mt-2">{error?.message || 'The strategic pillar could not be found'}</p>
        <button onClick={() => setView({ page: 'Strategic Pillars' })} className="mt-4 text-[#681628] hover:underline">
          &larr; Back to Strategic Pillars
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-500">
        <span className="cursor-pointer hover:underline" onClick={() => setView({ page: 'Strategic Pillars' })}>Strategic Pillars</span>
        <span className="mx-2">&gt;</span>
        <span className="font-medium text-gray-700">{article.title}</span>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
        <div className="flex items-start gap-6">
          {article.coverImage && (
            <img src={article.coverImage} alt={article.title} className="w-40 h-28 object-cover rounded-lg" />
          )}
          <div className="flex-1">
            <div className="flex justify-between items-start">
              <div>
                 <span className="text-xs font-semibold bg-green-100 text-green-700 px-2.5 py-1 rounded-full">
                   {article.status === 'PUBLISH' ? 'Published' : 'Draft'}
                 </span>
                 <h2 className="text-2xl font-bold text-gray-800 mt-2">{article.title}</h2>
              </div>
              <button className="text-gray-500 hover:text-gray-800"><MoreIcon /></button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Posted {formatDate(article.createdAt)} | Posted by {article.createdBy}
            </p>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Settings</h3>
            <div className="space-y-2">
                <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.landingPagePrioritiesOfAfsa}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Landing Page / Priorities of AfSA</span>
                </label>
                 <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.whoWeAre}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Who We Are</span>
                </label>
                 <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.countries}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Countries</span>
                </label>
                 <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.history}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">History</span>
                </label>
                 <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={article.member}
                      disabled
                      className="h-4 w-4 rounded border-gray-300 text-[#681628] focus:ring-[#681628]"
                    />
                    <span className="ml-2 text-sm text-gray-700">Member</span>
                </label>
            </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Cover Image Title</h3>
            <p className="text-sm text-gray-700">{article.coverImageTitle || 'No cover image title'}</p>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Article Tags</h3>
            <div className="flex flex-wrap gap-2">
              {Array.isArray(article.articleTag) && article.articleTag.length > 0 ? (
                article.articleTag.map((tag:string, index: string) => (
                  <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 text-sm rounded-full">
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-sm text-gray-500">No tags</span>
              )}
            </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
            <h3 className="font-semibold text-gray-800 mb-3">Content</h3>
            <div
              className="prose prose-sm max-w-none text-gray-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
        </div>
      </div>
    </div>
  );
};

export default StrategicPillarDetailPage;
